import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { getVendorApplications, updateApplicationStatus, getIncompleteDrafts, DraftData } from '@/data/vendorApplications';
import { VendorApplicationRow, OfferingTypeLabels, SaleMethodLabels, ProductionRangeLabels, TargetAgentTypeLabels, TracksOutcomesLabels, OfferingType, SaleMethod, ProductionRange, TargetAgentType, TracksOutcomes } from '@/contracts/vendor-application';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';
import { Loader2, CheckCircle, XCircle, AlertCircle, Eye, FileEdit, Mail, ExternalLink } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

// Step names for the canonical flow
const STEP_NAMES = [
  'Welcome / Company Info',
  'Offering Type',
  'Sale Method',
  'Ideal Agent (Fingerprint)',
  'Proof & Tracking',
  'Pricing',
  'Commercial Terms',
  'Categories',
  'Listing Builder',
  'Partner Call',
];

function DraftDetailModal({ 
  draft, 
  open, 
  onOpenChange 
}: { 
  draft: DraftData | null; 
  open: boolean; 
  onOpenChange: (open: boolean) => void;
}) {
  if (!draft) return null;
  
  const formData = draft.form_data || {};
  const currentStep = draft.current_step || 0;
  
  const formatValue = (value: unknown): string => {
    if (value === null || value === undefined || value === '') return '—';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'number') return value.toLocaleString();
    if (Array.isArray(value)) {
      if (value.length === 0) return '—';
      return value.join(', ');
    }
    return String(value);
  };

  const getOfferingTypeLabel = (type: string | undefined) => {
    if (!type) return '—';
    return OfferingTypeLabels[type as OfferingType] || type;
  };

  const getSaleMethodLabel = (method: string | undefined) => {
    if (!method) return '—';
    return SaleMethodLabels[method as SaleMethod] || method;
  };

  const getProductionRangeLabels = (ranges: string[] | undefined) => {
    if (!ranges || ranges.length === 0) return '—';
    return ranges.map(r => ProductionRangeLabels[r as ProductionRange] || r).join(', ');
  };

  const getTargetAgentLabels = (types: string[] | undefined) => {
    if (!types || types.length === 0) return '—';
    return types.map(t => TargetAgentTypeLabels[t as TargetAgentType] || t).join(', ');
  };

  const getTracksOutcomesLabel = (val: string | undefined) => {
    if (!val) return '—';
    return TracksOutcomesLabels[val as TracksOutcomes] || val;
  };

  const DataRow = ({ label, value }: { label: string; value: string }) => (
    <div className="flex justify-between py-1.5 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-right max-w-[60%]">{value}</span>
    </div>
  );

  const SectionHeader = ({ title, stepNum, isCompleted }: { title: string; stepNum: number; isCompleted: boolean }) => (
    <div className="flex items-center gap-2 mb-2">
      <Badge variant={isCompleted ? "default" : "outline"} className={isCompleted ? "bg-green-500/10 text-green-500" : "bg-muted text-muted-foreground"}>
        Step {stepNum}
      </Badge>
      <span className="font-semibold">{title}</span>
      {stepNum === currentStep + 1 && (
        <Badge className="bg-orange-500/10 text-orange-500 ml-auto">Stopped Here</Badge>
      )}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>Draft Application: {draft.company_name || 'Unknown Company'}</span>
          </DialogTitle>
          <DialogDescription className="flex items-center gap-4">
            <span>Session: {draft.session_id.slice(0, 8)}...</span>
            <span>•</span>
            <span>Started {formatDistanceToNow(new Date(draft.created_at), { addSuffix: true })}</span>
            <span>•</span>
            <Badge className="bg-orange-500/10 text-orange-500">
              Stopped at Step {currentStep + 1} of {STEP_NAMES.length}
            </Badge>
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1 pr-4 -mr-4">
          <div className="space-y-6 pr-4">
            {/* Step 1: Company Info */}
            <div>
              <SectionHeader title="Company & Contact Info" stepNum={1} isCompleted={currentStep >= 1} />
              <Card>
                <CardContent className="pt-4 space-y-1">
                  <DataRow label="Company Name" value={formatValue(formData.company_name)} />
                  <DataRow label="Company Website" value={formatValue(formData.company_website)} />
                  <DataRow label="Year Founded" value={formatValue(formData.company_year_founded)} />
                  <DataRow label="Location" value={formatValue(formData.company_location)} />
                  <Separator className="my-2" />
                  <DataRow label="Contact Name" value={formatValue(formData.contact_name)} />
                  <DataRow label="Contact Email" value={formatValue(formData.contact_email)} />
                  <DataRow label="Contact Title" value={formatValue(formData.contact_title)} />
                  <DataRow label="Contact Phone" value={formatValue(formData.contact_phone)} />
                </CardContent>
              </Card>
            </div>

            {/* Step 2: Offering Type */}
            <div>
              <SectionHeader title="Offering Type" stepNum={2} isCompleted={currentStep >= 2} />
              <Card>
                <CardContent className="pt-4 space-y-1">
                  <DataRow label="Offering Type" value={getOfferingTypeLabel(formData.offering_type)} />
                  <DataRow label="Service Type" value={formatValue(formData.service_type)} />
                  <DataRow label="Product Name" value={formatValue(formData.product_name)} />
                </CardContent>
              </Card>
            </div>

            {/* Step 3: Sale Method */}
            <div>
              <SectionHeader title="Sale Method" stepNum={3} isCompleted={currentStep >= 3} />
              <Card>
                <CardContent className="pt-4 space-y-1">
                  <DataRow label="Sale Method" value={getSaleMethodLabel(formData.sale_method)} />
                </CardContent>
              </Card>
            </div>

            {/* Step 4: Fingerprint */}
            <div>
              <SectionHeader title="Ideal Agent (Fingerprint)" stepNum={4} isCompleted={currentStep >= 4} />
              <Card>
                <CardContent className="pt-4 space-y-1">
                  <DataRow label="Production Ranges" value={getProductionRangeLabels(formData.production_ranges)} />
                  <DataRow label="Target Agent Types" value={getTargetAgentLabels(formData.target_agent_types)} />
                  <DataRow label="Primary Pain Point" value={formatValue(formData.primary_pain)} />
                  <DataRow label="Best Customer Description" value={formatValue(formData.best_customer_description)} />
                  <DataRow label="Target Audience" value={formatValue(formData.target_audience)} />
                  <DataRow label="Problem Solved" value={formatValue(formData.problem_solved)} />
                </CardContent>
              </Card>
            </div>

            {/* Step 5: Tracking */}
            <div>
              <SectionHeader title="Proof & Tracking" stepNum={5} isCompleted={currentStep >= 5} />
              <Card>
                <CardContent className="pt-4 space-y-1">
                  <DataRow label="Tracks Outcomes" value={getTracksOutcomesLabel(formData.tracks_outcomes)} />
                  <DataRow label="Customer Count" value={formatValue(formData.customer_count)} />
                  <DataRow label="Average Rating" value={formatValue(formData.average_rating)} />
                  <DataRow label="Prior LTD Experience" value={formatValue(formData.prior_ltd_experience)} />
                </CardContent>
              </Card>
            </div>

            {/* Step 6: Pricing */}
            <div>
              <SectionHeader title="Pricing" stepNum={6} isCompleted={currentStep >= 6} />
              <Card>
                <CardContent className="pt-4 space-y-1">
                  <DataRow label="Public Price" value={formData.public_price ? `$${formData.public_price}` : '—'} />
                  <DataRow label="Circle Pro Price" value={formData.circle_pro_price ? `$${formData.circle_pro_price}` : '—'} />
                  <DataRow label="Current Price" value={formData.current_price ? `$${formData.current_price}` : '—'} />
                  <DataRow label="Proposed Deal Price" value={formData.proposed_deal_price ? `$${formData.proposed_deal_price}` : '—'} />
                  <DataRow label="Pricing Model" value={formatValue(formData.pricing_model)} />
                  <DataRow label="Base Price Note" value={formatValue(formData.base_price_note)} />
                </CardContent>
              </Card>
            </div>

            {/* Step 7: Commercial Terms */}
            <div>
              <SectionHeader title="Commercial Terms" stepNum={7} isCompleted={currentStep >= 7} />
              <Card>
                <CardContent className="pt-4 space-y-1">
                  <DataRow label="Terms Acknowledged" value={formatValue(formData.commercial_terms_acknowledged)} />
                  <DataRow label="Agreed to Terms" value={formatValue(formData.agreed_to_terms)} />
                  <DataRow label="Consent to Processing" value={formatValue(formData.consent_to_processing)} />
                </CardContent>
              </Card>
            </div>

            {/* Step 8: Categories */}
            <div>
              <SectionHeader title="Categories & Discovery" stepNum={8} isCompleted={currentStep >= 8} />
              <Card>
                <CardContent className="pt-4 space-y-1">
                  <DataRow label="Primary Category" value={formatValue(formData.product_category)} />
                  <DataRow label="Secondary Categories" value={formatValue(formData.secondary_categories)} />
                </CardContent>
              </Card>
            </div>

            {/* Step 9: Listing Builder */}
            <div>
              <SectionHeader title="Listing Builder" stepNum={9} isCompleted={currentStep >= 9} />
              <Card>
                <CardContent className="pt-4 space-y-1">
                  <DataRow label="What This Is" value={formatValue(formData.what_this_is)} />
                  <DataRow label="Who It's For" value={formatValue(formData.who_its_for)} />
                  <DataRow label="What Success Looks Like" value={formatValue(formData.what_success_looks_like)} />
                  <DataRow label="What Happens After Purchase" value={formatValue(formData.what_happens_after_purchase)} />
                  <Separator className="my-2" />
                  <DataRow label="Brief Description" value={formatValue(formData.product_brief_description)} />
                  <DataRow label="Detailed Description" value={formatValue(formData.product_detailed_description)} />
                  <DataRow label="Key Features" value={formatValue(formData.key_features)} />
                  <DataRow label="Deal Includes" value={formatValue(formData.deal_includes)} />
                </CardContent>
              </Card>
            </div>

            {/* Step 10: Partner Call */}
            <div>
              <SectionHeader title="Partner Call" stepNum={10} isCompleted={currentStep >= 10} />
              <Card>
                <CardContent className="pt-4 space-y-1">
                  <DataRow label="Call Booked" value={formatValue(formData.partner_call_booked)} />
                  <DataRow label="Call DateTime" value={formatValue(formData.partner_call_datetime)} />
                </CardContent>
              </Card>
            </div>

            {/* Assets */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Badge variant="outline">Assets</Badge>
                <span className="font-semibold">Uploaded Files & URLs</span>
              </div>
              <Card>
                <CardContent className="pt-4 space-y-1">
                  <DataRow label="Logo URL" value={formatValue(formData.logo_url)} />
                  <DataRow label="Demo Video URL" value={formatValue(formData.demo_video_url)} />
                  <DataRow label="Product Website" value={formatValue(formData.product_website)} />
                  <DataRow label="Product Demo URL" value={formatValue(formData.product_demo_url)} />
                  <DataRow label="Terms URL" value={formatValue(formData.terms_url)} />
                  <DataRow label="Privacy URL" value={formatValue(formData.privacy_url)} />
                  <DataRow label="Screenshots" value={formData.screenshots?.length ? `${formData.screenshots.length} uploaded` : '—'} />
                </CardContent>
              </Card>
            </div>
          </div>
        </ScrollArea>

        <DialogFooter className="mt-4 pt-4 border-t">
          {draft.contact_email && (
            <Button variant="outline" asChild>
              <a href={`mailto:${draft.contact_email}?subject=Your Circle Pro Hub Partner Application`}>
                <Mail className="h-4 w-4 mr-2" />
                Contact Applicant
              </a>
            </Button>
          )}
          {formData.company_website && (
            <Button variant="outline" asChild>
              <a href={formData.company_website} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-4 w-4 mr-2" />
                Visit Website
              </a>
            </Button>
          )}
          <Button onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function PartnerApplications() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [applications, setApplications] = useState<VendorApplicationRow[]>([]);
  const [filteredApplications, setFilteredApplications] = useState<VendorApplicationRow[]>([]);
  const [drafts, setDrafts] = useState<DraftData[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedApp, setSelectedApp] = useState<VendorApplicationRow | null>(null);
  const [selectedDraft, setSelectedDraft] = useState<DraftData | null>(null);
  const [draftDetailOpen, setDraftDetailOpen] = useState(false);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewAction, setReviewAction] = useState<string>('');
  const [reviewNotes, setReviewNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('applications');
  const { toast } = useToast();

  useEffect(() => {
    if (!isAdmin) {
      navigate('/');
      return;
    }
    loadData();
  }, [isAdmin, navigate]);

  useEffect(() => {
    filterApplications();
  }, [applications, statusFilter, searchTerm]);

  const loadData = async () => {
    try {
      const [appsData, draftsData] = await Promise.all([
        getVendorApplications(),
        getIncompleteDrafts(),
      ]);
      setApplications(appsData);
      setDrafts(draftsData);
    } catch (error) {
      toast({
        title: 'Error Loading Data',
        description: error instanceof Error ? error.message : 'Failed to load data',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const filterApplications = () => {
    let filtered = applications;
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(app => app.status === statusFilter);
    }
    
    if (searchTerm) {
      filtered = filtered.filter(app =>
        app.company_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.product_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.contact_email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    setFilteredApplications(filtered);
  };

  const handleReview = (app: VendorApplicationRow, action: string) => {
    setSelectedApp(app);
    setReviewAction(action);
    setReviewNotes('');
    setReviewDialogOpen(true);
  };

  const handleViewDraft = (draft: DraftData) => {
    setSelectedDraft(draft);
    setDraftDetailOpen(true);
  };

  const submitReview = async () => {
    if (!selectedApp) return;
    
    setSubmitting(true);
    try {
      await updateApplicationStatus(selectedApp.id, reviewAction, reviewNotes);
      toast({
        title: 'Application Updated',
        description: `Application has been marked as ${reviewAction}`,
      });
      setReviewDialogOpen(false);
      loadData();
    } catch (error) {
      toast({
        title: 'Update Failed',
        description: error instanceof Error ? error.message : 'Failed to update application',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-500/10 text-yellow-500';
      case 'under_review': return 'bg-blue-500/10 text-blue-500';
      case 'approved': return 'bg-green-500/10 text-green-500';
      case 'rejected': return 'bg-red-500/10 text-red-500';
      case 'needs_info': return 'bg-orange-500/10 text-orange-500';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto py-8 flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 space-y-6">
      <div>
        <h1 className="text-3xl font-bold mb-2">Partner Applications</h1>
        <p className="text-muted-foreground">Review and manage vendor partnership applications</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="applications">
            Applications ({applications.length})
          </TabsTrigger>
          <TabsTrigger value="drafts">
            <FileEdit className="h-4 w-4 mr-2" />
            Incomplete ({drafts.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="applications" className="space-y-4 mt-4">
          <div className="flex gap-4">
            <div className="flex-1">
              <Input
                placeholder="Search by company, product, or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[200px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="under_review">Under Review</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="needs_info">Needs Info</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-4">
        {filteredApplications.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center text-muted-foreground">
              No applications found
            </CardContent>
          </Card>
        ) : (
          filteredApplications.map((app) => (
            <Card key={app.id}>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle>{app.company_name} - {app.product_name}</CardTitle>
                    <CardDescription>
                      {app.contact_name} ({app.contact_email}) • Applied {new Date(app.created_at).toLocaleDateString()}
                    </CardDescription>
                  </div>
                  <Badge className={getStatusColor(app.status)}>
                    {app.status.replace('_', ' ')}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-semibold">Category:</span> {app.product_category}
                  </div>
                  <div>
                    <span className="font-semibold">Type:</span> {app.service_type}
                  </div>
                  <div>
                    <span className="font-semibold">Current Price:</span> ${app.current_price}
                  </div>
                  <div>
                    <span className="font-semibold">Deal Price:</span> ${app.proposed_deal_price}
                  </div>
                </div>
                <p className="text-sm">{app.product_brief_description}</p>
                
                <div className="flex gap-2 pt-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setSelectedApp(app);
                      // Could open a detailed view dialog here
                    }}
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    View Details
                  </Button>
                  {app.status === 'pending' && (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleReview(app, 'under_review')}
                      >
                        <AlertCircle className="h-4 w-4 mr-2" />
                        Review
                      </Button>
                      <Button
                        size="sm"
                        variant="default"
                        onClick={() => handleReview(app, 'approved')}
                      >
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleReview(app, 'rejected')}
                      >
                        <XCircle className="h-4 w-4 mr-2" />
                        Reject
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
          </div>
        </TabsContent>

        <TabsContent value="drafts" className="space-y-4 mt-4">
          {drafts.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                No incomplete applications
              </CardContent>
            </Card>
          ) : (
            drafts.map((draft) => (
              <Card key={draft.id}>
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle>{draft.company_name || 'Unknown Company'}</CardTitle>
                      <CardDescription>
                        {draft.contact_email || 'No email yet'} • Step {draft.current_step + 1} of {STEP_NAMES.length} • Last active {formatDistanceToNow(new Date(draft.last_activity_at), { addSuffix: true })}
                      </CardDescription>
                    </div>
                    <Badge className="bg-orange-500/10 text-orange-500">
                      Incomplete
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex justify-between items-center">
                    <div className="text-sm text-muted-foreground">
                      Started {formatDistanceToNow(new Date(draft.created_at), { addSuffix: true })}
                      {draft.current_step < STEP_NAMES.length && (
                        <span className="ml-2">• Stopped at: <strong>{STEP_NAMES[draft.current_step]}</strong></span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleViewDraft(draft)}
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        View Draft
                      </Button>
                      {draft.contact_email && (
                        <Button size="sm" variant="outline" asChild>
                          <a href={`mailto:${draft.contact_email}?subject=Your Circle Pro Hub Partner Application`}>
                            <Mail className="h-4 w-4 mr-2" />
                            Contact
                          </a>
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>
      </Tabs>

      {/* Draft Detail Modal */}
      <DraftDetailModal 
        draft={selectedDraft} 
        open={draftDetailOpen} 
        onOpenChange={setDraftDetailOpen} 
      />

      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {reviewAction === 'approved' && 'Approve Application'}
              {reviewAction === 'rejected' && 'Reject Application'}
              {reviewAction === 'under_review' && 'Move to Review'}
            </DialogTitle>
            <DialogDescription>
              {selectedApp?.company_name} - {selectedApp?.product_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="review_notes">Review Notes (optional)</Label>
              <Textarea
                id="review_notes"
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                rows={4}
                placeholder="Add any notes about this decision..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={submitReview} disabled={submitting}>
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Updating...
                </>
              ) : (
                'Confirm'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
